import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Divider,
  Grid,
  Alert,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { reservationApi } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import { useTranslation } from 'react-i18next';
import { APP_ROUTES } from '../routing/appRoutes';

export const ReservationConfirmationPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: reservation, isLoading, error } = useQuery(
    ['reservation', id],
    () => reservationApi.getById(id!),
    { enabled: !!id }
  );

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography>{t('common.loading')}</Typography>
      </Container>
    );
  }

  if (error || !reservation) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{t('reservationConfirmation.notFound')}</Alert>
        <Button onClick={() => navigate(APP_ROUTES.public.search)} sx={{ mt: 2 }}>
          {t('common.backToSearch')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          {t('reservationConfirmation.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('reservationConfirmation.body')}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={2} sx={{ textAlign: 'left', mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">{t('reservations.reservationNumber')}</Typography>
            <Typography variant="h6">{reservation.reservationNumber}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">{t('fields.totalAmount')}</Typography>
            <Typography variant="h6" color="primary">
              {formatCurrency(reservation.totalAmount)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">{t('fields.vehicle')}</Typography>
            <Typography variant="body1">
              {reservation.vehicle?.make} {reservation.vehicle?.model} ({reservation.vehicle?.year})
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">{t('fields.rentalPeriod')}</Typography>
            <Typography variant="body1">
              {formatDate(reservation.startDate)} -{' '}
              {reservation.endDate ? formatDate(reservation.endDate) : t('rental.returnDateTbd')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">{t('fields.pickupLocation')}</Typography>
            <Typography variant="body1">{reservation.pickupLocation}</Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(APP_ROUTES.public.search)}
            sx={{ mr: 2 }}
          >
            {t('reservationConfirmation.bookAnother')}
          </Button>
          <Button variant="outlined" size="large" onClick={() => window.print()}>
            {t('reservationConfirmation.print')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

